import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@bliks-io/ui';
import {
  DataSyncUnitTypeSchema,
  type DataSyncDifference,
  type DataSyncRun
} from '@bliks-io/contracts';
import { SYNC_STEPS, selectedCount, unsuppliedPeople } from './syncSelection';
import { UnsuppliedPeopleNote } from './UnsuppliedPeopleNote';

type UnitDifference = Extract<DataSyncDifference, { kind: 'unitDelete' | 'unitLead' }>;

/** The selected differences of `kind`, named by unit type: "2 departments, 1 team". */
function namedUnits(
  differences: DataSyncDifference[],
  selected: ReadonlySet<string>,
  kind: UnitDifference['kind'],
  t: TFunction
) {
  const ofKind = differences.filter(
    (difference): difference is UnitDifference =>
      difference.kind === kind && selected.has(difference.id)
  );
  const units = DataSyncUnitTypeSchema.options.flatMap((type) => {
    const count = ofKind.filter(({ unit }) => unit.type === type).length;
    return count > 0 ? [t(`dataSync.sync.unitCount.${type}`, { count })] : [];
  });
  return { count: ofKind.length, units: units.join(', ') };
}

/** One line per step; the deletes name what they delete, and lead changes get a line of their own. */
function confirmLines(
  differences: DataSyncDifference[],
  selected: ReadonlySet<string>,
  t: TFunction
) {
  const leads = namedUnits(differences, selected, 'unitLead', t);
  const deletes = namedUnits(differences, selected, 'unitDelete', t);
  return SYNC_STEPS.flatMap((step) => {
    const line = {
      id: step.id,
      label: t(step.confirmKey),
      count: selectedCount(differences, selected, step),
      destructive: step.destructive
    };
    if (step.id === 'removals' && deletes.count > 0) {
      return [
        {
          ...line,
          label: t('dataSync.sync.confirm.deleteNamedUnits', { units: deletes.units })
        }
      ];
    }
    if (step.id !== 'changes') return [line];
    const people = differences.filter(
      ({ kind, id }) => kind === 'personUpdate' && selected.has(id)
    ).length;
    const leadLine = {
      id: 'leads',
      label: t('dataSync.sync.confirm.changeNamedLeads', { units: leads.units }),
      count: leads.count,
      destructive: false
    };
    return [{ ...line, count: people }, ...(leads.count > 0 ? [leadLine] : [])];
  });
}

function ConfirmLine({
  label,
  count,
  destructive
}: {
  label: string;
  count: number;
  destructive: boolean;
}) {
  return (
    <li
      className={cn(
        'flex items-center justify-between rounded-md border border-line-subtle px-3 py-2.5 text-sm',
        destructive && 'border-danger-line bg-danger-surface text-danger'
      )}
    >
      <span>{label}</span>
      <b className="tabular-nums">{count}</b>
    </li>
  );
}

/** Bestätigen: what the apply writes, step by step, and who the source cannot supply. */
export function SyncConfirmStep({
  run,
  selected
}: {
  run: DataSyncRun;
  selected: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ul className="flex flex-col gap-2">
        {confirmLines(run.differences, selected, t).map(({ id, ...line }) => (
          <ConfirmLine key={id} {...line} />
        ))}
      </ul>
      {!run.hasDownloadList && (
        <p className="mt-4 text-sm text-muted-foreground">
          {t('dataSync.sync.confirm.noDownloadList', {
            button: t('dataSync.settings.downloadExcel')
          })}
        </p>
      )}
      <UnsuppliedPeopleNote people={unsuppliedPeople(run)} source={run.sourceKind} />
    </>
  );
}
